import { orderBy } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import {
    H_HYDRO_MODULE,
    H_INSTALLATION_MODULE,
    H_PIEZO_MODULE,
    H_PLUVIO_MODULE,
    H_QUALITO_MODULE,
} from '../../../../account/constants/AccessRulesConstants'
import Checkbox from '../../../../components/forms/Checkbox'
import Input from '../../../../components/forms/Input'
import Select from '../../../../components/forms/Select'
import SimpleDatePicker from '../../../../components/forms/SimpleDatePicker'
import Textarea from '../../../../components/forms/Textarea'
import { componentHasHabilitations } from '../../../../utils/HabilitationUtil'
import { getObjectLabel } from '../../../../utils/StoreUtils'
import { Grid2, Stack } from '@mui/material'
import { MATERIEL_STATE_ON_SITE_ID, MATERIEL_STATE_PROVISIONAL_ID, MATERIEL_STATE_WARRANTY_ID } from 'materiel/constants/MaterielStateConstants'
import { STATION_TYPE_CONSTANT } from 'station/constants/StationConstants'

const SituationForm = ({
    situation = {},
    situationMaterialReplace = {},
    onChange = () => { },
    serialNumber = '',
    showCheckbox = false,
    shouldReplace = false,
    shouldUsePreviousParam = false,
    materialOnSite = [],
    stations = [],
    isCentral = false,
}) => {
    const {
        materialState,
        citiesIndex,
        contributors,
    } = useSelector(store => ({
        materialState: store.MaterielReducer.materielStates,
        citiesIndex: store.CityReducer.citiesIndex,
        contributors: store.ContributorReducer.contributors,
    }), shallowEqual)

    const stationInput = useMemo((() => {
        switch (situation.statusCode) {
            case MATERIEL_STATE_ON_SITE_ID: case MATERIEL_STATE_PROVISIONAL_ID:
                const stationFormat = stations.map(s => {
                    const labelTownCode = getObjectLabel(citiesIndex[s.townCode])
                    return {
                        ...s,
                        label: `${labelTownCode ? `${labelTownCode}${s.name || s.code ? ' - ' : ''}` : ''}${s.name || ''} ${s.code ? `[${s.code}]` : ''}`,
                    }
                })
                return (
                    <Select
                        value={situation.siteCode}
                        label={i18n.assignment}
                        options={stationFormat}
                        keyValue='id'
                        keyLabel='label'
                        onChange={(_, v) => onChange({
                            shouldReplace: false,
                            shouldUsePreviousParam: false,
                            situationMaterialReplace: {},
                            situation: {
                                ...situation,
                                siteCode: v && v.id,
                                siteName: v ? `${v.code ? `[${v.code}${v.designation ? `/${v.designation}` : ''}] - ` : ''}${v.name || ''}` : '',
                            },
                        })}
                    />
                )
            case MATERIEL_STATE_WARRANTY_ID:
                return (
                    <Grid2 container>
                        <Grid2 size={5}>
                            <Input
                                value={serialNumber}
                                title={i18n.serialNumber}
                            />
                        </Grid2>
                    </Grid2>
                )
            default:
                return null
        }
    }), [situation])

    const stationTypes = [{
        code: STATION_TYPE_CONSTANT.PIEZOMETRY,
        label: i18n.piezometry,
        habilitation: H_PIEZO_MODULE,
    }, {
        code: STATION_TYPE_CONSTANT.PLUVIOMETRY,
        label: i18n.pluviometry,
        habilitation: H_PLUVIO_MODULE,
    }, {
        code: STATION_TYPE_CONSTANT.QUALITY,
        label: i18n.quality,
        habilitation: H_QUALITO_MODULE,
    }, {
        code: STATION_TYPE_CONSTANT.HYDROMETRY,
        label: i18n.hydrometry,
        habilitation: H_HYDRO_MODULE,
    }, {
        code: STATION_TYPE_CONSTANT.INSTALLATION,
        label: i18n.installation,
        habilitation: H_INSTALLATION_MODULE,
    }].filter(t => componentHasHabilitations(t.habilitation))

    return (
        <Stack spacing={2}>
            <Grid2 container spacing={2}>
                <Grid2 size={5}>
                    <Select
                        options={orderBy(materialState, 'code', 'asc')}
                        label={i18n.state}
                        onChange={value => {
                            if (value == MATERIEL_STATE_WARRANTY_ID) {
                                onChange({
                                    shouldReplace: false,
                                    shouldUsePreviousParam: false,
                                    situationMaterialReplace: {},
                                    situation: {
                                        ...situation,
                                        comment: serialNumber,
                                        statusCode: parseInt(value),
                                        siteType: undefined,
                                        siteCode: undefined,
                                        siteName: undefined,
                                    },
                                })
                            } else {
                                onChange({ situation: { ...situation, statusCode: parseInt(value) } })
                            }
                        }}
                        value={situation.statusCode}
                        nullLabel='&nbsp;'
                    />
                </Grid2>
                {
                    (situation.statusCode === MATERIEL_STATE_ON_SITE_ID || situation.statusCode === MATERIEL_STATE_PROVISIONAL_ID) && (
                        <Grid2 size={7}>
                            <Select
                                options={stationTypes}
                                label={i18n.assignmentType}
                                onChange={siteType => onChange({
                                    shouldReplace: false,
                                    shouldUsePreviousParam: false,
                                    situationMaterialReplace: {},
                                    situation: {
                                        ...situation,
                                        siteType,
                                        siteCode: undefined,
                                        siteName: undefined,
                                    },
                                })}
                                value={situation.siteType}
                                nullLabel='&nbsp;'
                            />
                        </Grid2>
                    )
                }
            </Grid2>
            {(!!situation.siteType || situation.statusCode === MATERIEL_STATE_WARRANTY_ID) && stationInput}
            <Grid2 container spacing={2}>
                <Grid2 size={3}>
                    <SimpleDatePicker
                        id='situationDate'
                        value={situation.situationDate}
                        label={i18n.startDate}
                        onChange={value => onChange({ situation: { ...situation, situationDate: moment(value).valueOf() } })}
                    />
                </Grid2>
                <Grid2 size={9}>
                    <Select
                        value={situation.administratorCode}
                        label={i18n.administrator}
                        options={contributors}
                        keyValue='id'
                        displayWithCode
                        onChange={v => onChange({ situation: { ...situation, administratorCode: v } })}
                    />
                </Grid2>
            </Grid2>
            {
                (situation.statusCode === 1 || situation.statusCode === 4) && showCheckbox && (
                    <Grid2 container spacing={2}>
                        <Grid2 size={4}>
                            <Checkbox
                                label={i18n.replaceMaterial}
                                checked={shouldReplace}
                                onChange={v => onChange({ shouldReplace: v, situationMaterialReplace: {} })}
                            />
                        </Grid2>
                        {
                            shouldReplace && (
                                <Grid2 size={4}>
                                    <Select
                                        options={materialOnSite}
                                        label={i18n.materialToReplace}
                                        onChange={value => onChange({ situationMaterialReplace: { materielId: value } })}
                                        value={situationMaterialReplace.materielId}
                                        nullLabel='&nbsp;'
                                    />
                                </Grid2>
                            )
                        }
                        {
                            !!situationMaterialReplace.materielId && (
                                <Grid2 size={4}>
                                    <Select
                                        options={materialState.filter(s => s.code !== 1 && s.code !== 4)}
                                        label={i18n.newSituation}
                                        onChange={value => onChange({ situationMaterialReplace: { ...situationMaterialReplace, statusCode: value } })}
                                        value={situationMaterialReplace.statusCode}
                                        nullLabel='&nbsp;'
                                    />
                                </Grid2>
                            )
                        }
                    </Grid2>
                )
            }
            {
                (situation.statusCode === 1 || situation.statusCode === 4) && showCheckbox && shouldReplace && isCentral && !!situationMaterialReplace.materielId && (
                    <Grid2 container spacing={2}>
                        <Grid2 size={8}>
                            <Checkbox
                                label={i18n.usePreviousParam}
                                checked={shouldUsePreviousParam}
                                onChange={v => onChange({ shouldUsePreviousParam: v })}
                            />
                        </Grid2>
                    </Grid2>
                )
            }
            <Textarea
                title={i18n.comment}
                value={situation.comment}
                onChange={value => onChange({ situation: { ...situation, comment: value } })}
            />
        </Stack>
    )
}

SituationForm.propTypes = {
    situation: PropTypes.shape({}),
    situationMaterialReplace: PropTypes.shape({}),
    onChange: PropTypes.func,
    serialNumber: PropTypes.string,
    showCheckbox: PropTypes.bool,
    shouldReplace: PropTypes.bool,
    shouldUsePreviousParam: PropTypes.bool,
    materialOnSite: PropTypes.arrayOf(PropTypes.shape({})),
    stations: PropTypes.arrayOf(PropTypes.shape({})),
    isCentral: PropTypes.bool,
}

export default SituationForm
